import { Boss, type BossDef } from './boss-base';
import { getTexture } from '@/assets/resolver';
import type { BulletSystem } from '@/entities/bullets';
import type { TelegraphSink } from '@/render/telegraphs';

/**
 * Stalker — a cloaked hunter. It fades near-invisible and slinks to the
 * player's flank, then DECLOAKS for a telegraphed ambush dash + a quick fan,
 * before vanishing again. Always damageable; the challenge is tracking it.
 * docs/02 boss pool.
 * Signatures: cloak → flickering tell → ambush dash + bolt fan (always).
 */
export const STALKER: BossDef = {
  id: 'stalker',
  nameKey: 'boss.stalker',
  tier: 3,
  minRoom: 2,
  baseHp: 1300,
  radius: 40,
  bodyKey: 'boss/stalker/body',
  movement: 'orbit',
  contactDamage: 1,
  phases: [
    { hpPct: 1.0, patterns: [] },
    { hpPct: 0.5, speedMul: 1.15, patterns: [] },
  ],
  evolve: [
    {
      // Marksman: a telegraphed single-shot burst from the shadows
      tier: 2,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/bolt', count: 1, spreadDeg: 0,
        interval: 3.4, telegraph: 0.5, burst: 2, burstGap: 0.2,
        speed: 300, radius: 9, damage: 1,
      },
    },
    {
      // Caltrops: a slow bolt ring scattered once it is wounded
      tier: 3,
      fromPhase: 1,
      pattern: {
        type: 'radial', bullet: 'bullet/enemy/bolt', count: 8, interval: 4.4,
        spinDeg: 22, speed: 105, radius: 9, damage: 1,
      },
    },
  ],
};

const CLOAK_TIME = 2.6;
const TELEGRAPH = 0.55; // decloak flash before the lunge
const STRIKE_TIME = 0.6;
const DRIFT = 150;
const DASH = 560;
const FAN_SPEED = 340;
const FAN_OFFSETS: ReadonlyArray<number> = [-0.3, 0, 0.3];

type State = 'cloak' | 'tell' | 'strike';

export class StalkerBoss extends Boss {
  private state: State = 'cloak';
  private timer = CLOAK_TIME;
  private flankSide = 1;
  private dashX = 0;
  private dashY = 0;
  private fired = false;

  constructor(level: number, x: number, y: number, homeX: number, homeY: number) {
    super(STALKER, level, x, y, homeX, homeY);
    this.allowReinforce = false;
  }

  override update(
    dt: number, px: number, py: number, bullets: BulletSystem, telegraphs?: TelegraphSink,
  ): void {
    super.update(dt, px, py, bullets, telegraphs);
    if (this.dead) return;
    this.timer -= dt;
    switch (this.state) {
      case 'cloak':
        if (this.timer <= 0) { this.state = 'tell'; this.timer = TELEGRAPH; this.fired = false; }
        break;
      case 'tell':
        if (this.timer <= 0) {
          // Lock the lunge vector at the player and pounce
          const d = Math.hypot(px - this.x, py - this.y) || 1;
          this.dashX = (px - this.x) / d;
          this.dashY = (py - this.y) / d;
          this.state = 'strike';
          this.timer = STRIKE_TIME;
          this.fan(px, py, bullets);
        }
        break;
      case 'strike':
        if (this.timer <= 0) {
          this.state = 'cloak';
          this.timer = CLOAK_TIME / this.intensity;
          this.flankSide = -this.flankSide;
        }
        break;
    }
  }

  private fan(px: number, py: number, bullets: BulletSystem): void {
    if (this.fired) return;
    this.fired = true;
    const base = Math.atan2(py - this.y, px - this.x);
    for (const off of FAN_OFFSETS) {
      bullets.spawn({
        x: this.x, y: this.y, dirX: Math.cos(base + off), dirY: Math.sin(base + off),
        speed: FAN_SPEED * this.bulletSpeedMul, life: 4, radius: 8, damage: 1,
        friendly: false, texKey: 'bullet/enemy/bolt', rotates: true,
      });
    }
  }

  protected override move(dt: number, px: number, py: number): void {
    if (this.state === 'strike') {
      this.x += this.dashX * DASH * this.levelSpeedMul * dt;
      this.y += this.dashY * DASH * this.levelSpeedMul * dt;
      return;
    }
    // Cloak/tell: slink toward the player's flank
    const ang = Math.atan2(this.y - py, this.x - px) + this.flankSide * 0.9;
    const tx = px + Math.cos(ang) * 300;
    const ty = py + Math.sin(ang) * 300;
    const sp = DRIFT * this.levelSpeedMul;
    const dx = tx - this.x;
    const dy = ty - this.y;
    const d = Math.hypot(dx, dy) || 1;
    this.x += (dx / d) * Math.min(d, sp * dt);
    this.y += (dy / d) * Math.min(d, sp * dt);
  }

  override render(alpha: number): void {
    super.render(alpha);
    const visible = this.state !== 'cloak';
    this.body.texture = getTexture('boss/stalker/body', visible ? 1 : 0);
    this.body.rotation = Math.atan2(
      this.y - this.prevY, this.x - this.prevX,
    ) + Math.PI / 2;
    // Cloak shimmer; the tell flickers brighter as the strike nears
    this.body.alpha = this.state === 'cloak'
      ? 0.22
      : this.state === 'tell'
        ? 0.5 + Math.sin(this.timer * 40) * 0.4
        : 1;
    void alpha;
  }
}
